import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../components/dialog/dialog";
import { DialogContextProviderProps, DialogOptions, DialogStateSetters } from "./types";

export const DialogStateSetterContext = createContext<DialogStateSetters>(undefined);
DialogStateSetterContext.displayName = "DialogStateSetterContext";

/**
 * A context provider that provides a dialog UI that can be triggered from anywhere within its children sub-tree using
 * convenience functions. These convenience functions can be access via the following companion hook.
 *
 * ### `useDialog`
 *
 * This hook return the following two functions:
 *
 * - `openDialog`
 * - `closeDialog`
 *
 * The `openDialog` function takes a `DialogOptions` object as it's parameter. The properties of this options object
 * cover key props of the `Dialog` component thus allowing full customization of the dialog content and interactivity.
 *
 * The `closeDialog` function can be tied to custom application logic to perform useful actions before the dialog is
 * closed.
 */
export const DialogContextProvider = ({ children }: DialogContextProviderProps) => {
  const [open, setOpen] = useState(false);
  const dialogOptions = useRef<DialogOptions>(undefined);

  const openDialog = useCallback<DialogStateSetters["openDialog"]>((options) => {
    dialogOptions.current = options;
    setOpen((open) => open && false); // close dialog if already open
    setOpen(true);
  }, []);

  const closeDialog = useCallback<DialogStateSetters["closeDialog"]>((event?: React.SyntheticEvent<HTMLElement>) => {
    setOpen(false);
    dialogOptions.current?.onClose?.(event);
  }, []);

  const stateSetters = useMemo<DialogStateSetters>(() => ({ openDialog, closeDialog }), [closeDialog, openDialog]);

  const { body, ...restOptions } = dialogOptions.current || {};

  return (
    <DialogStateSetterContext.Provider value={stateSetters}>
      {children}
      <Dialog modal={true} {...restOptions} open={open} onClose={closeDialog}>
        {body}
      </Dialog>
    </DialogStateSetterContext.Provider>
  );
};
